import { useState, useEffect, Fragment } from 'react'
import { ArrowPathIcon, LockClosedIcon, LockOpenIcon, HomeIcon, PencilIcon, MapIcon, DocumentTextIcon, ListBulletIcon, RectangleStackIcon, XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/solid'

const useWindowResize = () => {
    const [dimension, setDimension] = useState([window.innerWidth, window.innerHeight]);

    useEffect(() => {
        window.addEventListener("resize", () => {
            setDimension([window.innerWidth, window.innerHeight])
        });
        return () => {
            window.removeEventListener("resize", () => {
                setDimension([window.innerWidth, window.innerHeight])
            })
        }
    }, []);
    
    return dimension;
}


import { sortBy, all, identity, join } from 'ramda'
const shuffle = sortBy(() => (Math.random()))
const joinWithSpace = join(' ')

class Thennable {
  constructor(arg) { this.thing = arg }
  then(fun) { 
    const unwrapped = this.unwrap()
    const thenned = fun(unwrapped)
    const rewrapped = wrap(thenned)
    return rewrapped
  }
  unwrap() { return this.thing }
  tap(fun) { fun(this.thing); return this }
  debug(label, mapFun) { 
    mapFun = mapFun ?? ((z) => (z))
    if (label) { 
      console.log(`${label}: ${mapFun(this.thing)}`) 
    } else {
      console.log(mapFun(this.thing))
    }
    return wrap(this.thing)
  }
}
const wrap = (thing) => (new Thennable(thing))

const duplicatesRemoved = (array, mapFun) => {
  mapFun = (mapFun ?? ((z) => (z)))
  const mappedArray = array.map(mapFun)

  return array.filter((v, i) => (mappedArray.indexOf(mapFun(v)) === i))
}

const roll = (min, max) => (Math.floor(Math.random() * (max - min + 1) + min));

const natureKey = ({group, name}) => (`${group}/${name}`)

// `‡` is traumatized trait
// `ᛉ` is supernatural trait.
const groupedNatures = [
  { groupName: "Comfortable", natures: [
    { name: "Farm",
      description: "A _farm_ is a place where people live unremarkable lives, deeply rooted in the turning of the seasons and the work that must be done.",
      canAlways: [
        "Describe the calm mundanity of everyday life.",
        "Reveal the thin margins people live on.",
        `Ask: "Hey, wanna help out?" Give them a token if they pitch in, in whatever way they
        can.`,
      ],
      aestheticChoices: [
        "Rapscallions And Merrymakers",
        "Crops As Far As The Eye Can See",
        "Peaceful Livestock",
        "Rusty Overgrown Weapons Of War",
        "A House You Once Called Home",
        "A Childhood Inspiration, Now Washed-Up And _Pensive_",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Scarecrow That Walked Away",
        "The Rain Of A Hundred Days",
        "The Apple-Girl And Her Loving Parents",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Garden", 
      description: "A _garden_ is a place where everyone has plenty and the world is overflowing with gifts.",
      arrival: "When you arrive at this place, decide what it has in abundance.",
      canAlways: [
        "Describe the abundance all around us.",
        "Threaten the health and vitality of the abundance.",
        `Ask: "What do you need most right now?". Give them a token if they can't find it here.`
      ],
      aestheticChoices: [
        "Trees Overburdened With Fruit",
        "Outrageously Large Gourds",
        "Prismatic Butterflies",
        "Carefully-Trimmed Hedfes",
        "Irrigating Fountains",
        "A _Friendly_ Soul Unfamiliar With The Hardship Of The World",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Peach Tree's Prophetic Blossom",
        "The Old God In The Old Tree",
        "The Burning Of The Corn Worman",
        "Something Else Of Your Own Invention",
      ]
    },
    { name: "Market",
      description: "A market is a place where near-anything can be traded and bartered, and where people from across the Hæth gather with supplies to exchange.",
      canAlways: [
        "Describe what you can find here.",
        "Reveal the consequences of theft.",
        "Offer someone something they want, at an affordable price. Give them a token if they refuse the offer.",
      ],
      aestheticChoices: [
        "Show-Stopping Livestock",
        "Foreign Wares",
        "Confusing Currencies",
        "Antique Relics",
        "Distant Smells",
        "A Familiar _Crafty_ Merchant",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Eel Daemon And The Lutist",
        "The Crow And The Thirty-Three Thieves",
        "The Golden King And How He Lost His Heart",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Monastery", 
      description: "A _monastery_ is a place where a community gathers to learn from the past and cultivate answers to the complicated questions of the world.",
      canAlways: [
        "Describe the rhythm of daily life.",
        "Show tensions caused by generational divides.",
        `Ask: "Are you willing to wait to get the answer you seek?" Give them a token if they're here to wait. Do not tell them the answer either way.`
      ],
      aestheticChoices: [
        "The Hermit's Cottage",
        "Booming Bells",
        "A Simple Herb Garden",
        "Scriptorium Filled With Knowledge",
        "Underground Catacombs",
        "A _Wise_ Elder Who Remembers You From When You Were Very Young",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Generous Mentor And Her Betrayal",
        "The Janitor And The Secret Lessons He Taught",
        "The Alabaster Monk",
        "Something Else Of Your Own Invention",
      ], 
    },
    { name: "Tower",
      description: "A _tower_ is a place that reaches so high up to the sky that it feels like it scrapes against the clouds.",
      canAlways: [
        "Describe something very small and far away.",
        "Spread concern around structural stability.",
        "Make someone dizzy or disoriented, and give them a token.",
      ],
      aestheticChoices: [
        "Narrow Barely-Stable Entrance",
        "Winding Staircase All The Way To The Top",
        "Shining Light Warning Of Danger",
        "Roosting Moths In The Rafters",
        "Not Enough Space For All The Clutter",
        "A ‡_Grieving_ Soul Responsible For Keeping Everything In Working Order",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Observatory Of The Lightning Dancers",
        "The First Of The Moth-Towers",
        "The Ghost That Walks The Stairs",
        "Something Else Of Your Own Invention",
      ], 
    },
    { name: "Workshop", 
      description: "A _workshop_ is a place where fine crafts and wares are created and given life, and where people value their ability to breathe life into art.",
      canAlways: [
        "Describe the process of creation.",
        "Show conflict between expectations and demand.",
        "Give someone a token if they work hard and sweat over their creation.",
      ],
      aestheticChoices: [
        "A Whirring Loom",
        "A Chugging Factory",
        "A Work Song",
        "An Important Supply Line",
        "A _Cunning_ Kid Too Young To Be Working",
        "An _Imaginative_ Craftsperson Who Made One of Your Treasures",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Crafter Who Made Herself A Bride",
        "The Potter And The Goddess",
        "The Neverending Tapestry",
        "Something Else Of Your Own Invention",
      ], 
    },
  ] },
  { groupName: "Verdant", natures: [
    { name: "Field",
      description: "A _field_ is a place to lie back, feel the breeze in your fur, and look up at the sky.",
      canAlways: [
        "Describe a gentle place to rest.",
        "Show someone looking for something lost.",
        `Ask: "Can we look at the clouds together?" Give them a token if they say yes.`,
      ],
      aestheticChoices: [
        "Rustling Grass",
        "A Babbling Brook",
        "A Solitary Tree",
        "Wild Plants And Herbs",
        "Mossy Boulders",
        "A _Friendly_ Shepherd And Their Flock",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Lucky Shepherd's Friend",
        "The Barrow-Ghosts",
        "The Dance Of The Flower Goddess",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Glen",
      description: "A glen is a place overflowing with creatures and bugs, alive with movement and presence.",
      canAlways: [
        "Describe a world teeming with life.",
        "Show a conflict between wild creatures.",
        "Introduce a new buggy creature. Give someone a token if they engage with it as an equal.",
      ],
      aestheticChoices: [
        "Chittering Mantises",
        "Cautious Pillbugs",
        "Massive Beetles",
        "_Watchful_ Caterpillars",
        "Soaring Dragonflies",
        "A ᛉ_Feral_ And Remarkable Creature That No One Has Seen For Hundreds Of Years",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Meadow Of The Lightning Dancers",
        "The Last Of The Monarch Butterflies",
        "The Day The Woods Walked",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Hallow",
      description: "A _hallow_ is a sacred place, where gods and mortals mingle.",
      arrival: "When you arrive at this place, decide a rule that all must follow.",
      canAlways: [
        "Describe the gods that live here and their mystery.",
        "Offer someone the chance to break this place's rules to make their life easier.",
        "Give someone a token when they bend over backwards to respect this place's rules.",
      ],
      aestheticChoices: [
        "A Well-Tended Shrine",
        "A Worn-Away Mask",
        "_Watchful_ Eyes In The Shadows",
        "An Unexplainable Miracle",
        "Forgotten Offerings",
        "An Old _Sturdy_ Caretaker-Priest",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The ᛉ_Miraculous_ Teachings Of The Old Priest",
        "The Fifteen Sleeping Gods",
        "The Maiden And Her Salmon-Headed Daemon Love",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Hillock",
      description: "A _hillock_ is a place in the foothills of a great and looming presence.",
      canAlways: [
        "Describe what we stand in the shadows of.",
        "Show a threat that's up ahead.",
        `Ask: "What are you worried lies up ahead?" and give them a token if they're right.`,
      ],
      aestheticChoices: [
        "A Sheltered Valley",
        "Strange Piles Of Stones",
        "A Deep Gorge",
        "Scattered Fragments Of Civilization",
        "Evidence Of Distant Strangeness",
        "A _Quiet_ Foreigner From Higher-Up",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Goat Who Slept For A Hundred Years",
        "The Fallen Star And The Stories She Told",
        "The Hidden Treasure Of The Hyena-King",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Lagoon",
      description: "A _lagoon_ is a place of contemplation, introspection, and self-reflection.",
      canAlways: [
        "Describe the water and all the tiny creatures that live in it.",
        "Make matters worse through people's inaction.",
        `Ask: "What have you been thinking about lately?" and give them a token for their thoughts.`,
      ],
      aestheticChoices: [
        "Mossy Stones",
        "A Hidden Grotto",
        "A Glorious Waterfall",
        "A Rare And Mysterious Flower",
        "A Well-Worn Path",
        "A _Watchful_ Creature Who Hides From The Outside",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Gifting Of The Heavenblade",
        "The Teacher Who Lives Within The Rocks",
        "The Waterfall's Broken Heart",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Swamp",
      description: "A _swamp_ is a place where the air is as thick as the mud.",
      canAlways: [
        "Describe the heaviness of the world.",
        "Show tension caused by stagnation.",
        "Bog someone down, and give them a token.",
      ],
      aestheticChoices: [
        "Muck And Mire",
        "A Pungent Stench",
        "A Heavy Fog",
        "Wriggling Worms",
        "A Sinking Feeling",
        "A _Cunning_ And Tricksy Spirit Offering To Help",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Treacherous Lights Deep In The Dark",
        "The Traveling Bog",
        "The Shrewish Witch And Her Magic Cabin",
        "Something Else Of Your Own Invention",
      ],
    },
  ] },
  { groupName: "Liminal", natures: [
    { name: "Bridge",
      description: "A _bridge_ is a passage from one place to the next.",
      canAlways: [
        "Describe the distance to the other side.",
        "Push something off the edge.",
        "Offer someone an easier way. Give someone a token if they don't take it.",
      ],
      aestheticChoices: [
        "Ancient Engineering",
        "Rickety Planks",
        "A Sudden Drop",
        "A Tollbooth",
        "Churning Waters Far Below",
        "A ᛉ_Mighty_ Creature Hiding Underneath",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Bargain Of The Flat-Faced Bass Daemon",
        "The Path Carved By The Giant-King",
        "The Fox And Toad Crossing",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Island",
      description: "An island is a place kept secret and separate from the rest of the Hæth.",
      arrival: "When you arrive at this place, decide what makes it feel different than what you're used to.",
      canAlways: [
        "Describe the comfort of an isolated world.",
        "Show conflict caused by isolation.",
        "Give someone a token when they respect this place's difference.",
      ],
      aestheticChoices: [
        "Concealed Entrance",
        "A Forbidden Secret",
        "A Hidden Gem",
        "A Looming God",
        "An _Inquisitive_ Kid Who Has Never Seen The Outside World",
        "A _Proper_ Leader Who Is Afraid Of The Outside World",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Cave Where The Storms Stay At Night",
        "The First Survivor To Wash Ashore",
        "The Faith Of The White Dress",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Lake",
      description: "A _lake_ is a vast body of water that spans an overwhelming distance.",
      canAlways: [
        "Describe the beauty of the water.",
        "Wash something strange up on shore.",
        "Bring someone somewhere completely unexpected, and give them a token.",
      ],
      aestheticChoices: [
        "Huge Churning Waves",
        "Sandy Coastline",
        "Flotsam And Jetsam",
        "Striders Darting Across The Surface",
        "Barnacle-Covered Rocks",
        "A Nearby Vessel You Weren't Expecting To See",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Kraken From The Deep",
        "The Salmon With Three Wishes",
        "The Hubris Of The Warthog Captain",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Port",
      description: "A _port_ is the gateway through which the whole world opens up to you.",
      canAlways: [
        "Describe the ships and where they go.",
        "Show the dangers of a fragile lifestyle.",
        `Ask: "Can I take you somewhere you've never been before?" Give them a token if they accept.`,
      ],
      aestheticChoices: [
        "Chunky Barges",
        "Hot Air Balloons",
        "Jetties And Docks",
        "Fidgeting Hoppers",
        "Smug Captains",
        "An _Adventurous_ Buddy You Once Wandered The World With",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Headless Spirit Of The Dockyard",
        "The Boat That Would Not Sink",
        "The Eastern Mist And The Trouble It Brings",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Road",
      description: "A _road_ is a place that exists for traveling through.",
      canAlways: [
        "Describe something passing by.",
        "Keep someone from getting where they're trying to go.",
        "Push someone forward, and give them a token.",
      ],
      aestheticChoices: [
        "Cairns Along The Path",
        "Litter In The Gutters",
        "A Lively Waterway",
        "Grooves In The Ground",
        "A _Friendly_ Caravan",
        "An Old _Relaxed_ Comrade Heading In",
        "Different Direction",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Day The Hounddog Met His Double",
        "The Possum Made Of Mist",
        "The Pact Of The Crossroads Shrine",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Tavern",
      description: "A tavern is a place of simple comfort, often settled into while on the journey from one land to the next.",
      canAlways: [
        "Describe a small comfort.",
        "Sow confusion amongst the disoriented.",
        "Offer someone comfort and amenities, at a price. Give them a token if they refuse your offer.",
      ],
      aestheticChoices: [
        "Dry Places To Sleep",
        "Huge Fireplace With A Big Pot Of Stew",
        "Barrels And Barrels Of Mead",
        "Great Hall Jam-Packed With Layabouts",
        "Bar Rat With A Secret Identity",
        "A _Cheerful_ Innkeep Who You Once Called Family",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Night The Old King Drank Here",
        "The Cat With The Magic Ale",
        "How The Old Wombat Outdrank The Slobbering God",
        "Something Else Of Your Own Invention",
      ],
    },
  ] },
  { groupName: "Sprawling", natures: [
    { name: "Carnival",
      description: "A _carnival_ is a place full of celebration, decadence, and excitement.",
      canAlways: [
        "Describe the lights and the crowds.",
        "Demand attention and adoration.",
        `Ask: "What will lure you deeper into the chaos?" Give them a token if they follow it.`,
      ],
      aestheticChoices: [
        "Strange Actors",
        "Extravagant Displays Of Excess",
        "A Mysterious Performance",
        "A Hungry Crowd",
        "Masquerade Accoutrements",
        "A ᛉ_Glamorous_ Friend Who Has Reinvented Themself",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Girl With A Thousand Masks",
        "The Neverending Wine Glass",
        "The Slobbering God And The Immorality Feast",
        "Something Else Of Your Own Invention",
      ],
      
    },
    { name: "Castle",
      description: "A _castle_ is a place designed to keep everything else far outside its walls.",
      canAlways: [
        "Describe the walls that surround us.",
        "Spread worry about a distant threat.",
        "Keep someone from entering, and give them a token.",
      ],
      aestheticChoices: [
        "Long Shadows",
        "Trophies From Long-Forgotten Wars",
        "Watchtowers",
        "A Secret Heart",
        "The Crest Of A Distant Monarch",
        "A ‡_Furious_ Administrator Who Oversees All",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Murdered Brother",
        "The Ghost On The Parapets",
        "The Lost Empire Of The Brick-Layers",
        "Something Else Of Your Own Invention",
      ],
    },
    { name: "Furnace",
      description: "A _furnace_ burns hot with the force of furious and creative fire.",
      canAlways: [
        "Describe the stifling heat.",
        "Raise tensions caused by intolerable conditions.",
        `Ask: "Will you forge what once was broken?" Give them a token if they try their best.`
      ],
      aestheticChoices: [
        "A Seething, Flaming Forge",
        "Walls Of Iron Tools",
        "Ornate Glassworks",
        "A Legendary Blade, Left Unfinished",
        "An _Imaginative_ Apprentice Still Learning The Craft",
        "A ᛉ_Mighty_ Smith With A Burning Grudge",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices:[
        "The Eternal Forge And Its Iron Servants",
        "The Heavenblade And Its Construction",
        "The Binding Of The Slobbering God",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Metropolis",
      description: "A _metropolis_ is a place where a lot of people live and where many communities sit side-by-side.",
      canAlways: [
        "Describe the diversity and variety of people.",
        "Show conflict between differing needs and worldviews.",
        "Tell someone they're lost, and give them a token."
      ],
      aestheticChoices: [
        "Tall Ramshackle Apartments",
        "Curving Arches",
        "Cobblestone Streets",
        "Laundry Hanging From Windows",
        "More People Than You've Ever Met Before",
        "The Enclave Of A Community You're Proud To Consider Yourself Part Of",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Truce Of The Three Gods",
        "The Night-Goat Of Vermilion Street",
        "The Giant Scorpions In The Sewer",
        "Something Else Of Your Own Invention"
      ]
    },    
    {
      name: "Palace",
      description: "A _palace_ is a place ruled by power. It is beautiful, yet empty.",
      arrival: "When you arrive at this place, decide who claims to rule it, and who really rules it.",
      canAlways: [
        "Describe the stunning splendor of the world.",
        "Show the aftermath of a conflict between its two rulers.",
        "Ask: \"What here makes you feel small?\" and give them a token."
      ],
      aestheticChoices: [
        "Glorious Halls",
        "Many Colorful Flags",
        "Resounding Trumpets",
        "A Crystal Crown",
        "A Gleaming Throne",
        "The Young And ‡_Royal_ Heir Dreaming Of Freedom",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Throne Of Crystal And Its Many Lies",
        "The King Who Killed A God",
        "The Crumbling Dynasty Of The Floating Mountain",
        "Something Else Of Your Own Invention",
      ]
    },    
    {
      name: "University",
      description: "A university is a place where many gather to learn from ancient texts.",
      canAlways: [
        "Describe the vast amount of knowledge still left to learn.",
        "Show conflict between differing philosophies or viewpoints.",
        `Ask: "Do you want to learn a difficult or painful truth?" Give them a token if they listen.`,
      ],
      aestheticChoices: [
        "Grassy Campus Quad",
        "Too-Small Dormitories",
        "Vast Banquet Hall With Many Long Tables",
        "Statues Of The Founders In All Their Glory",
        "An Inquisitive And Hopeful Kid Who Doesn't Want To Be Here",
        "A ᛉWitchy Professor Whom You Once Called A Mentor",
        "Something Else Of Your Own Invention",
      ],
      folkloreChoices: [
        "The Founders And Their Petty Squabbles",
        "The Forbidden Society Of The Theatre Hall",
        "That One Prank That Got Far Too Out Of Hand",
        "Something Else Of Your Own Invention",
      ]
    },
  ]},
  { groupName: "Lonely", natures: [
    {
      name: "Cave",
      description: "A _cave_ is a place fundamentally connected to the core of the earth, its wisdom, and its tenebrous secrets.",
      canAlways: [
        "Describe the beauty of the deepest shadows of the world.",
        "Swallow something into the dark.",
        `Ask: "Will you wander ever deeper?" Give them a token if they say yes.`
      ],
      aestheticChoices: [
        "Darkness Deeper Than You Expected",
        "A Bridge From This World To The Next",
        "Petroglyphs",
        "Countless Fungi",
        "The Voice Of Faraway Winds",
        "A ‡_Cautious_ Hermit",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Tapping On The Walls",
        "The First Art And The Stories It Tells",
        "The Sleeping God At The Bottom Of The World",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Graveyard",
      description: "A _graveyard_ is a place of death, where the lost are buried.",
      canAlways: [
        "Describe the weight of the past.",
        "Show someone disrespecting the history of this place.",
        `Ask: "Will you tell us a story no one else could tell?" Give them a token if they do.`
      ],
      aestheticChoices: [
        "Scattered Cairns",
        "Melted Candles",
        "Gravestones",
        "Mysterious Lights",
        "Deep And Profound Silence",
        "An Old Forgotten Friend, Now ‡_Dead_",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Candlelit Council And Their Warning",
        "The Day The Dead Danced With Us",
        "The Mist Rabbit And Her Rictus Grin",
        "Something Else Of Your Own Invention"
      ]
    },    
    {
      name: "Mirage",
      description: "A _mirage_ is a place that doesn't really exist.",
      canAlways: [
        "Describe something too good to be true.",
        "Show someone lost in their folly.",
        `Decide if something in front of someone is real, and then ask: "Do you believe what you see?" Give them a token if they're wrong.`
      ],
      aestheticChoices: [
        "Too-Vivid Flowers",
        "Too-Shining Ramparts",
        "Too-Verdant Plantlife",
        "Too-Satisfying Food",
        "Too-Beautiful Music",
        "Too-Friendly Memory Of A Person",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Palace Of The Saccharine Dead",
        "The City Of Restless Dreams",
        "The Emperor's False Promise To His Adoring Lover",
        "Something Else Of Your Own Invention"
      ]
    },    
    {
      name: "Mirror",
      description: "A _mirror_ is a place that reflects you back onto yourself.",
      canAlways: [
        "Describe what looks back.",
        "Reflect back something someone didn't want to see.",
        `Ask: "What is it about your reflection that unnerves you?" and give them a token.`
      ],
      aestheticChoices: [
        "Glassy Water",
        "Salt Flats",
        "Perfect Calmness",
        "Scratches In The Reflection",
        "The Sensation Of An Unfinished World",
        "Your Doppelganger",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The March Of The Countless Footsteps",
        "The Ghost Whose Name Cannot Be Spoken",
        "The Very Edge Of The World Itself",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Moor",
      description: "A _moor_ is a quiet place, where sharp winds whistle through a stretched-out landscape.",
      canAlways: [
        "Describe what makes someone feel lonely.",
        "Hint at something eerie.",
        `Ask: "What weighs on your mind?" Give them a token if they give a satisfying answer.`
      ],
      aestheticChoices: [
        "Distant Wasp-Caws",
        "Patches Of Wetland",
        "A Thin Mist",
        "Gnarled Trees",
        "A Shiver Down Your Back",
        "A ‡_Nervous_ Teen Sneaking Away For Their Love",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Devil-Dog And Its Prey",
        "The Heron And Her Murdered Lover",
        "The Lost Army Of The First Queen",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Wilderness",
      description: "A _wilderness_ is a place that holds nature close to its heart, closer than any mortal that dwells within.",
      canAlways: [
        "Describe something massive, sublime, and uncaring.",
        "Show the challenges caused by living with nature.",
        `Say: "Get out of the way!" and give everyone who does a token. Describe what enormous thundering force nearly strikes them down.`
      ],
      aestheticChoices: [
        "Tangled Undergrowth",
        "Trees Older Than Any Empire",
        "Something Bigger Than You've Ever Imagined",
        "Territorial Bugs",
        "A Pristine Waterfall",
        "A Creature Assumed To Be Extinct",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The God Of All The Forests",
        "The Forest Spirits And Their Mockery",
        "The Song At The Heart Of All Things",
        "Something Else Of Your Own Invention"
      ]
    }
    
  ]},
  { groupName: "Desolate", natures: [
    {
      name: "Desert",
      description: "A _desert_ is an empty place, without any of what you want or need.",
      canAlways: [
        "Describe the emptiness of the world.",
        "Show someone lost and wandering.",
        "Tell someone the path will be longer and more treacherous than they thought, and give them a token."
      ],
      aestheticChoices: [
        "Sand That Goes On Forever",
        "Floating Pillars Of Stone",
        "A Bitter Chill",
        "The Harsh Sun",
        "The Horizon Line",
        "A ‡_Starving_ Pilgrim With Murky Intentions",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Ghost-Storm And The Lost Caravan",
        "The Opah Daemon's Whispers To The Wandering Prophet",
        "The Impossible Rain",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Labyrinth",
      description: "A _labyrinth_ is a tangled maze used to trick, imprison, and confuse.",
      canAlways: [
        "Describe the vast sprawl of the maze.",
        "Disorient and confuse those attempting to travel.",
        "Give someone two options, neither of which is correct, and give them a token."
      ],
      aestheticChoices: [
        "Stone Walls",
        "Tangled Paths",
        "Unexpected Hazards",
        "Chalk-Marked Directions",
        "A ‡_Lost_ Innocent At The Heart Of It All",
        "A ‡_Heroic_ Guide Who Might Not Be Helpful",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Crow Inventor And Her Wax Wings",
        "The Paths That Grow Of Their Own Accord",
        "The Ninety-Nine Wailing Minnow Daemons",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Maelstrom",
      description: "A _maelstrom_ is a great and chaotic storm, full of winds and terrifying forces.",
      canAlways: [
        "Describe the storm that threatens us.",
        "Smash something against the rocks.",
        "Rip something away from someone, and give them a token."
      ],
      aestheticChoices: [
        "Churning Skies",
        "Howling Winds",
        "Screeching Wasps",
        "Bolts Of Lightning",
        "Flying Debris",
        "A ᛉ_Mighty_ God Spotted Only In Brief Moments",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Fury Of The North Wind God",
        "The Lightning Dancers And Their Beautiful War",
        "The Queen Who Ripped A Hole In The World",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Mountain",
      description: "A _mountain_ is a place where the earth meets the sky.",
      canAlways: [
        "Describe what can be seen from way up here.",
        "Show someone pushed to desperate extremes.",
        "Isolate someone from everyone else, and give them a token."
      ],
      aestheticChoices: [
        "Sharp Cliffs",
        "A Treacherous Path",
        "A Lonely Flower",
        "A Sign Someone Was Here Once",
        "An Even Higher Summit",
        "A ‡_Cautious_ Creature Hiding Just Out Of Sight",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Elk King Feasting Beneath The Hills",
        "The North Wind God And What He Taught Us",
        "Where The Gentle Giants Fought",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Ruin",
      description: "A _ruin_ is a once-gleaming place that has fallen into disrepair and collapse.",
      canAlways: [
        "Describe what used to be here before.",
        "Show consequences for the actions of the past.",
        `Ask: "Do you know what once mattered here?" Give them a token if they don't.`
      ],
      aestheticChoices: [
        "Crumbling Pillars",
        "Fragments Of Pottery And Bone",
        "Abandoned Blades",
        "Monuments To Arrogance",
        "The Foundations Of Something Massive",
        "The Last ‡_Grieving_ Survivor Of An Ancient Time",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Snowblanket Estate Of The Last Great Empress",
        "The Crypt Of The Dragons",
        "The Desecrated Temple Of The Slobbering God",
        "Something Else Of Your Own Invention"
      ]
    },
    {
      name: "Waste",
      description: "A _waste_ is a place that life has abandoned.",
      canAlways: [
        "Describe the barren earth.",
        "Keep something from growing or changing.",
        `Ask: "What do you see here that breaks your heart?" and give them a token.`
      ],
      aestheticChoices: [
        "Dead Trees",
        "Tiny Struggling Saplings",
        "Dull Skies",
        "Craters Blasted In The Ground",
        "Wisps Of Smoke",
        "A Fragile People Wandering The Lonely World",
        "Something Else Of Your Own Invention"
      ],
      folkloreChoices: [
        "The Curse Of The Lonely King",
        "The Corpse Of The Slobbering God Itself",
        "Where The Apocalypse Will Someday Ignite",
        "Something Else Of Your Own Invention"
      ]
    },    
  ]},
]

const allNatures = groupedNatures.flatMap(({ groupName, natures }) => (
  natures.map((n) => ({...n, group: groupName}))
))

window.allNatures = allNatures

const fillUntil = (original, cap) => {
  let newNatures = [...original]
  let count = 0
  while (newNatures.length < cap) {
    count += 1

    if (count > 10) {
      console.error("uh oh")
      return []
    }
    
    wrap(allNatures.length - 1)
      .then(($) => (roll(0, $)))
      .then(($) => (allNatures[$]))
      .then(($) => ({...$, uuid: self.crypto.randomUUID()}))
      .then(($) => (newNatures.push($)))
    newNatures = duplicatesRemoved(newNatures, natureKey)
  }
  return newNatures
}

const findNewNature = (oldNatures) => {
  let newNature = null
  let count = 0
  let maxCount = 10
  while (newNature == null) {
    count += 1
    if (count > maxCount) { console.error(`took more than ${maxCount} tries for random`); return null }

    const potential = 
      wrap(allNatures)
        .then(($) => (shuffle($)))
        .then(($) => ($[0]))
        .unwrap()

    const oldMap = oldNatures.map(natureKey)
    const newMap = natureKey(potential)
    const oldIncludesNew = oldMap.includes(newMap)

    if (!oldIncludesNew) {
      newNature = { ...potential, uuid: self.crypto.randomUUID() }
    }
  }
  return newNature
}

const expanded = "expanded"
const folded = "folded"

const modeListing = "listing"
const modeEditing = "editing"
const modeViewing = "viewing"

export const App = () => {
  const [natures, setNatures] = useState([])
  const [accordion, setAccordion] = useState([expanded, expanded, expanded])
  const [choices, setChoices] = useState({})
  const [mode, setMode] = useState(modeListing)
  const [limitNatures, setLimitNatures] = useState(true)

  const [windowWidth, windowHeight] = useWindowResize()

  const toggleChoice = (key) => {
    setChoices({...choices, [key]: !choices[key]})
  }

  const choiceIsToggled = (key) => ( choices[key] ?? false )

  const shuffleNatures = () => {
    const l = natures.length
    let n = [...(natures.filter(({uuid}) => (choiceIsToggled(`${uuid}/nature`))))]
    while (n.length < l) {
      let newN = findNewNature(n)
      if (limitNatures) { 
        if (["Comfortable", "Verdant", "Liminal"].includes(newN.group)) {
          n.push(newN) 
        }
      } else {
        n.push(newN)
      }
    }
    setNatures(n)
  }

  const shuffleNatureAtIndex = (index) => {
    let newN = null
    while (true) {
      newN = findNewNature(natures)
      if (limitNatures) { 
        if (["Comfortable", "Verdant", "Liminal"].includes(newN.group)) {
          break          
        }
      } else {
        break
      }
    }

    let newNatures = [...natures]
    newNatures[index] = {...newN, uuid: natures[index].uuid}
    setNatures(newNatures)
  }

  const addNewNature = () => {
    const uuid = self.crypto.randomUUID()
    setNatures([...natures, { uuid, name: null }])
    toggleChoice(`${uuid}/custom`)
  }

  useEffect(() => {
    addNewNature()
  }, [])

  return (
      <div className="max-w-[480px] px-2 mt-2 m-auto flex flex-col gap-2 justify-start items-stretch leading-5 mb-4">
        <div className="bg-stone-50 rounded-sm border border-stone-800 px-4 py-4 shadow-lg flex flex-col gap-4 ">
          <div className="font-bold text-lg leading-4">Wanderhome Place Generating Tool</div>
          <div>
          <div className="italic border-l-2 border-stone-300 pl-2 text-justify hyphens-auto">Natures are the building blocks of places. They inform the aesthetic trappings and symbols of the place, the world surrounding it, and how it shapes the people who engage with it. Comfortable, verdant, and liminal natures are common throughout the Hæth, while sprawling, lonely, and desolate natures are more rare.</div>
          <div className="text-stone-700 not-italic text-sm text-right">from <a href="https://possumcreekgames.com/pages/wanderhome" className="italic underline hover:bg-blue-200 rounded-sm px-1">Wanderhome</a> by Jay Dragon</div>
        
          </div>
          <div className="flex flex-col gap-4">
                  <div className="flex flex-row justify-between text-sm border border-stone-200 rounded-sm">
                    <div className="flex items-center px-2 break-none whitespace-nowrap">View mode</div>
                    <div className="grid grid-cols-3 gap-2 px-2 py-2 col-span-2 bg-stone-50 w-full">
                      <button disabled={mode === modeListing} className={"enabled:shadow-md rounded-sm text-sm enabled:hover:shadow-lg enabled:hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2 disabled:bg-stone-300"} onClick={ () => { 
                        setMode(modeListing)
                      }}><ListBulletIcon className="size-5" /> Overview</button>
                      <button disabled={mode === modeEditing} className={"enabled:shadow-md rounded-sm text-sm enabled:hover:shadow-lg enabled:hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2 disabled:bg-stone-300"} onClick={ () => { 
                        setMode(modeEditing)
                      }}><PencilIcon className="size-5" /> Choices</button>
                      <button disabled={mode === modeViewing} className={"enabled:shadow-md rounded-sm text-sm enabled:hover:shadow-lg enabled:hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2 disabled:bg-stone-300"} onClick={ () => { 
                        setMode(modeViewing)
                      }}><DocumentTextIcon className="size-5" /> Reading</button>
                  </div>
                </div>

                  <div className="flex flex-row justify-between text-sm border border-stone-200 rounded-sm">
                    <div className="flex flex-col justify-center px-2 break-none whitespace-nowrap">Allowed natures<div className="text-xs text-stone-500">(when randomizing)</div></div>
                    <div className="grid grid-cols-2 gap-2 px-2 py-2 col-span-2 bg-stone-50 w-full">
                  <button disabled={limitNatures == true} className={"enabled:shadow-md rounded-sm text-sm enabled:hover:shadow-lg enabled:hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2 disabled:bg-stone-300"} onClick={ () => { 
                    setLimitNatures(true)
                  }}>
                  <HomeIcon className="size-5" /> Common
                  </button>

                  <button disabled={limitNatures == false} className={"enabled:shadow-md rounded-sm text-sm enabled:hover:shadow-lg enabled:hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2 disabled:bg-stone-300"} onClick={ () => { 
                    setLimitNatures(false)
                  }}>
                    <MapIcon className="size-5" /> Everything
                  </button>
                  </div>
                </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <button className="shadow-md col-span-1 rounded-sm text-sm hover:shadow-lg hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2" onClick={ () => { 
            addNewNature()
          }}><PlusIcon className="size-5" /> Add nature</button>
          <button className="shadow-md col-span-1 rounded-sm text-sm hover:shadow-lg hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2" onClick={ () => { 
            const uuid = self.crypto.randomUUID()
            setNatures([{uuid}])
            toggleChoice(`${uuid}/custom`)
          }}><TrashIcon className="size-5" /> Reset All</button>
          <button className="shadow-md col-span-1 rounded-sm text-sm hover:shadow-lg hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2" onClick={ () => { 
            shuffleNatures()
          }}><ArrowPathIcon className="size-5" /> Shuffle natures</button>
        </div>
        </div>
        { natures.map((nature, index) => {
          const {uuid, group, name} = nature
          const isAccordionExpanded = accordion[index] === expanded
          const isAccordionFolded = accordion[index] === folded
          const expandAccordion = () => {
            let a = [...accordion]
            a[index] = expanded
            setAccordion(a)
          }
          const foldAccordion = () => {
            let a = [...accordion]
            a[index] = folded
            setAccordion(a)
          }
          return (
            <div key={uuid} className={"transition bg-stone-50 rounded-sm border border-stone-800 px-4 space-y-4 py-4 shadow-lg"}>
              <div className="text-stone-900 font-bold text-lg leading-4">{ choiceIsToggled(`${uuid}/custom`) && (<>Choose a nature</>) || (<>{ name }&nbsp;<span className="font-normal text-stone-600">({group})</span></>)}
                <button className="float-right shadow-md rounded-sm bg-stone-100 px-2 py-2 ml-2 border border-stone-800 self-start" onClick={() => {
                  if (nature.name == null) { 
                    let newNatures = [...natures]
                    newNatures.splice(index, 1)
                    setNatures(newNatures)
                  } else {
                    toggleChoice(`${uuid}/custom`)
                  }
                }}>
                  { choiceIsToggled(`${uuid}/custom`) && (<XMarkIcon className="size-5" />)}
                  { choiceIsToggled(`${uuid}/custom`) || (<RectangleStackIcon className="size-5" />)}
                  
                </button>

                {(nature.name != null) && (
                <button className="float-right ml-2 shadow-md col-span-1 rounded-sm text-sm hover:shadow-lg hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2" onClick={ () => { 
                  let n = [...natures]
                  n.splice(index, 1)
                  setNatures(n)
                }}><TrashIcon className="size-5" /></button>
                )}

                {choiceIsToggled(`${uuid}/custom`) || (
                <button className="float-right shadow-md rounded-sm bg-stone-100 px-2 py-2 ml-2 border border-stone-800 self-start" onClick={ () => { 
                  toggleChoice(`${uuid}/nature`)
                }}>
                  {choiceIsToggled(`${uuid}/nature`) && <LockClosedIcon className="size-5" />}
                  {choiceIsToggled(`${uuid}/nature`) || <LockOpenIcon className="size-5" />}
                </button>
                )}

                {choiceIsToggled(`${uuid}/custom`) && (
                <button className="float-right shadow-md col-span-3 rounded-sm text-sm hover:shadow-lg hover:bg-stone-200 bg-stone-100 px-2 py-2 border border-stone-800 flex items-center gap-2 font-normal" onClick={ () => { 
                            shuffleNatureAtIndex(index, "common")
                            toggleChoice(`${uuid}/custom`)
                        }}><ArrowPathIcon className="size-5" /> Random nature</button>
                        )}
              </div>
              { choiceIsToggled(`${uuid}/custom`) && (
                <div className="flex flex-col gap-4 clear-both">
                  {groupedNatures.map(({groupName, natures: groupNatures}, gIndex) => (
                    <div key={`f${gIndex}`} className="grid grid-cols-3 gap-2 mx-2">
                      <div className="col-span-3 ml-[-0.5rem]">{groupName}</div>
                      {groupNatures.map((nature, natureIndex) => (
                        <button key={`${gIndex}/${natureIndex}`} className="shadow-md rounded-sm bg-stone-100 px-2 py-2 border border-stone-800 self-start" onClick={ () =>  {
                          const thisUuid = self.crypto.randomUUID()
                          let n = [...natures]
                          n[index] = {...nature, group: groupName, uuid: thisUuid}
                          console.log(n)
                          setNatures(n)
                          toggleChoice(`${thisUuid}/nature`)
                        }}>
                        {nature.name}
                      </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              { mode !== modeListing && !choiceIsToggled(`${uuid}/custom`) && (
                <>
                  {nature.description && (<div className="text-stone-700">{nature.description}</div>)}
                  {nature.arrival && (<div className="text-stone-700">{nature.arrival}</div>)}
                  {nature.canAlways && (
                    <div>
                      <span className="font-bold">This place can always:</span>
                      <ul className="list-disc list-outside ml-5">
                        {nature.canAlways.map((ca, caIndex) => (
                          <li key={caIndex} className="my-0.5">{ca}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {
                  [
                    { list: nature.aestheticChoices, 
                      prompt: "Choose 2 aesthetic elements.", 
                      heading: "Aesthetic Elements", 
                      key: "aesthetic" },
                    { list: nature.folkloreChoices, 
                      prompt: "Choose 1 folklore about this place", 
                      heading: "Folklore about this place", 
                      key: "folklore" }
                  ].map(({list, prompt, heading, key}, index) => {
                    const filteredList = list.filter( (thisItem, thisIndex) => (mode == modeEditing || choiceIsToggled(`${uuid}/${key}/${thisIndex}`)) )
                    return (<Fragment key={index}>
                      { filteredList.length > 0 && (
                        <div>
                          <span className="font-bold">{mode == modeEditing ? prompt : heading}</span>
                          <ul className={
                            joinWithSpace([
                              "list-disc list-outside",
                              mode == modeEditing
                                ? "ml-0"
                                : "ml-5"
                            ])
                          }>
                            {filteredList.map((thisItem, thisIndex) => (
                              <li key={thisIndex} className={joinWithSpace([
                                "my-0.5 px-1 rounded-sm cursor-pointer",
                                mode == modeEditing ? (
                                choiceIsToggled(`${uuid}/${key}/${thisIndex}`)
                                  ? "bg-yellow-200 [@media(pointer:coarse)]:bg-yellow-200 [@media(hover:hover)]:bg-yellow-200" 
                                  : "[@media(hover:hover)]:hover:bg-yellow-100"
                                ) : "",
                                mode == modeEditing
                                  ? "list-none [@media(pointer:coarse)]:py-1 [@media(pointer:coarse)]:bg-stone-100"
                                  : "list-disc"
                              ])}
                                onClick={(e) => { toggleChoice(`${uuid}/${key}/${thisIndex}`); e.stopPropagation() }}>
                                  {mode == modeEditing && <input type="checkbox" className="mr-2 accent-yellow-700" 
                                    checked={choiceIsToggled(`${uuid}/${key}/${thisIndex}`)} 
                                    onChange={(e) => { toggleChoice(`${uuid}/${key}/${thisIndex}`); e.stopPropagation()}}
                                    />}{thisItem}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                    </Fragment>)})}
                </>
              )}
          </div>
        )}) }
    </div>
  )
}